// src/styles/theme.js - ENHANCED with consistent design system
import { createTheme } from '@mui/material/styles';

// Design tokens for consistency
const designTokens = {
    spacing: {
        xs: 4,
        sm: 8,
        md: 16,
        lg: 24,
        xl: 32,
        xxl: 48,
    },
    borderRadius: {
        sm: 8,
        md: 12,
        lg: 16,
        xl: 20,
        full: 9999,
    },
    shadows: {
        sm: '0 1px 3px 0 rgba(0,0,0,0.08), 0 1px 2px -1px rgba(0,0,0,0.05)',
        md: '0 4px 12px -2px rgba(0,0,0,0.12), 0 2px 6px -1px rgba(0,0,0,0.08)',
        lg: '0 10px 30px -8px rgba(0,0,0,0.16), 0 4px 12px -4px rgba(0,0,0,0.08)',
        xl: '0 20px 40px -12px rgba(0,0,0,0.20), 0 8px 16px -8px rgba(0,0,0,0.12)',
        glow: {
            primary: '0 0 20px rgba(37,99,235,0.25)',
            secondary: '0 0 20px rgba(5,150,105,0.25)',
            purple: '0 0 20px rgba(124,58,237,0.25)',
        },
        // Card-specific shadows for consistency
        card: {
            sm: '0 4px 12px -2px rgba(0,0,0,0.08)',
            md: '0 8px 24px -4px rgba(0,0,0,0.12)',
            lg: '0 8px 40px -8px rgba(0,0,0,0.15)',
            interactive: '0 8px 40px -8px rgba(0,0,0,0.15)',
            gradient: '0 8px 40px -8px rgba(37,99,235,0.4)',
        }
    },
    transitions: {
        fast: '150ms cubic-bezier(0.4, 0, 0.2, 1)',
        normal: '250ms cubic-bezier(0.4, 0, 0.2, 1)',
        slow: '350ms cubic-bezier(0.4, 0, 0.2, 1)',
    },
    // Consistent overlay colors
    overlays: {
        light: 'rgba(255, 255, 255, 0.15)',
        lighter: 'rgba(255, 255, 255, 0.10)',
        dark: 'rgba(0, 0, 0, 0.08)',
        darker: 'rgba(0, 0, 0, 0.12)',
        primary: 'rgba(37, 99, 235, 0.1)',
        primaryLight: 'rgba(37, 99, 235, 0.04)',
        secondary: 'rgba(5, 150, 105, 0.1)',
        secondaryLight: 'rgba(5, 150, 105, 0.04)',
    }
};

const theme = createTheme({
    palette: {
        primary: {
            main: '#2563eb',
            light: '#60a5fa',
            dark: '#1d4ed8',
            contrastText: '#ffffff',
        },
        secondary: {
            main: '#059669',
            light: '#34d399',
            dark: '#047857',
            contrastText: '#ffffff',
        },
        background: {
            default: '#f8fafc',
            paper: '#ffffff',
        },
        error:   { main: '#dc2626', light: '#ef4444', dark: '#b91c1c' },
        warning: { main: '#f59e0b', light: '#fbbf24', dark: '#d97706' },
        success: { main: '#10b981', light: '#34d399', dark: '#059669' },
        info:    { main: '#3b82f6', light: '#60a5fa', dark: '#2563eb' },
        
        // Risk zone colors
        riskZones: {
            green: '#10b981', 
            yellow: '#f59e0b',
            orange: '#f97316', 
            red: '#dc2626',
        },
        
        // Gradient colors
        gradients: {
            primary: 'linear-gradient(135deg, #2563eb 0%, #059669 100%)',
            secondary: 'linear-gradient(135deg, #7c3aed 0%, #db2777 100%)',
            warm: 'linear-gradient(135deg, #f97316 0%, #dc2626 100%)',
            cool: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
        },
        
        // Audience-specific colors
        audience: {
            researcher: '#3b82f6',
            student: '#8b5cf6',
            worker: '#059669',
            investor: '#dc2626',
        },
        // CTA button colors
        cta: {
            main: '#2563eb',
            gradient: 'linear-gradient(135deg, #2563eb 0%, #059669 100%)',
            background: 'white',
            text: '#1e40af',
        },
    },
    
    typography: {
        fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
        h1: {
            fontSize: 'clamp(2rem, 5vw, 3rem)',
            fontWeight: 800,
            lineHeight: 1.15,
            letterSpacing: '-0.02em',
        },
        h2: {
            fontSize: 'clamp(1.5rem, 3.5vw, 2.25rem)',
            fontWeight: 700,
            lineHeight: 1.25,
            letterSpacing: '-0.01em',
        },
        h3: {
            fontSize: 'clamp(1.2rem, 2.5vw, 1.6rem)',
            fontWeight: 600,
            lineHeight: 1.35,
        },
        h4: {
            fontSize: 'clamp(1rem, 2vw, 1.25rem)',
            fontWeight: 600,
            lineHeight: 1.4,
        },
        h5: {
            fontSize: 'clamp(0.95rem, 1.5vw, 1.1rem)',
            fontWeight: 600,
            lineHeight: 1.45,
        },
        h6: {
            fontSize: '1rem',
            fontWeight: 600,
            lineHeight: 1.5,
        },
        body1: { 
            fontSize: '1rem',
            lineHeight: 1.7,
            letterSpacing: '0.00938em',
        },
        body2: { 
            fontSize: '0.875rem',
            lineHeight: 1.65,
            letterSpacing: '0.01071em',
        },
        caption: { 
            fontSize: '0.75rem',
            lineHeight: 1.5,
            letterSpacing: '0.03em',
        },
        button: {
            fontSize: '0.875rem',
            fontWeight: 600,
            letterSpacing: '0.02em',
            textTransform: 'none',
        },
    },
    
    shape: { 
        borderRadius: designTokens.borderRadius.md,
    },
    
    // Custom spacing scale
    spacing: 8,
    
    components: {
        MuiButton: {
            styleOverrides: {
                root: {
                    textTransform: 'none',
                    fontWeight: 600,
                    padding: '10px 24px',
                    borderRadius: designTokens.borderRadius.md,
                    transition: `all ${designTokens.transitions.normal}`,
                    boxShadow: 'none',
                },
                contained: {
                    boxShadow: designTokens.shadows.sm,
                    '&:hover': {
                        boxShadow: designTokens.shadows.md,
                        transform: 'translateY(-1px)',
                    },
                    '&:active': {
                        transform: 'translateY(0)',
                        boxShadow: designTokens.shadows.sm,
                    },
                },
                outlined: {
                    borderWidth: '1.5px',
                    '&:hover': { 
                        borderWidth: '1.5px',
                        transform: 'translateY(-1px)',
                    },
                },
                sizeLarge: {
                    padding: '12px 32px',
                    fontSize: '1rem',
                },
                sizeSmall: {
                    padding: '6px 16px',
                    fontSize: '0.8125rem',
                },
            },
            variants: [
                {
                    props: { variant: 'cta' },
                    style: {
                        background: '#ffffff',
                        color: '#1e40af',
                        fontWeight: 700,
                        boxShadow: designTokens.shadows.sm,
                        padding: '10px 28px',
                        '&:hover': {
                            background: 'rgba(255, 255, 255, 0.95)',
                            boxShadow: designTokens.shadows.md,
                            transform: 'translateY(-1px)',
                        },
                        '&:active': {
                            transform: 'translateY(0)',
                            boxShadow: designTokens.shadows.sm,
                        },
                    },
                },
                {
                    props: { variant: 'ctaGradient' },
                    style: {
                        background: 'linear-gradient(135deg, #2563eb 0%, #059669 100%)',
                        color: '#ffffff',
                        fontWeight: 700,
                        boxShadow: designTokens.shadows.md,
                        padding: '10px 28px',
                        border: 'none',
                        '&:hover': {
                            boxShadow: designTokens.shadows.lg,
                            transform: 'translateY(-2px)',
                        },
                        '&:active': {
                            transform: 'translateY(0)',
                            boxShadow: designTokens.shadows.sm,
                        },
                    },
                },
            ],
        },
        
        MuiCard: {
            styleOverrides: {
                root: {
                    boxShadow: designTokens.shadows.sm,
                    borderRadius: designTokens.borderRadius.lg,
                    transition: `all ${designTokens.transitions.normal}`,
                    border: '1px solid rgba(0,0,0,0.04)',
                    '&:hover': {
                        boxShadow: designTokens.shadows.lg,
                        transform: 'translateY(-2px)',
                    },
                },
            },
        },
        
        MuiPaper: {
            styleOverrides: {
                root: { 
                    backgroundImage: 'none',
                },
                elevation1: {
                    boxShadow: designTokens.shadows.sm,
                },
                elevation2: {
                    boxShadow: designTokens.shadows.md,
                },
                elevation3: {
                    boxShadow: designTokens.shadows.lg,
                },
                rounded: {
                    borderRadius: designTokens.borderRadius.lg,
                },
            },
        },
        
        MuiTextField: {
            styleOverrides: {
                root: {
                    '& .MuiOutlinedInput-root': {
                        borderRadius: designTokens.borderRadius.md,
                        transition: `all ${designTokens.transitions.fast}`,
                        '&:hover': {
                            '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'rgba(37,99,235,0.3)',
                            },
                        },
                        '&.Mui-focused': {
                            boxShadow: '0 0 0 3px rgba(37,99,235,0.12)',
                            '& .MuiOutlinedInput-notchedOutline': {
                                borderWidth: '1.5px',
                            },
                        },
                    },
                },
            },
        },
        
        MuiChip: {
            styleOverrides: {
                root: { 
                    fontWeight: 600,
                    borderRadius: designTokens.borderRadius.sm,
                    transition: `all ${designTokens.transitions.fast}`,
                    '&:hover': {
                        transform: 'translateY(-1px)',
                        boxShadow: designTokens.shadows.sm,
                    },
                },
                outlined: {
                    borderWidth: '1.5px',
                },
            },
        },
        
        MuiAlert: {
            styleOverrides: {
                root: {
                    borderRadius: designTokens.borderRadius.md,
                    boxShadow: designTokens.shadows.sm,
                },
                filled: {
                    fontWeight: 500,
                },
            },
        },
        
        MuiDialog: {
            styleOverrides: {
                paper: {
                    borderRadius: designTokens.borderRadius.xl,
                    boxShadow: designTokens.shadows.xl,
                },
            },
        },
        
        MuiBottomNavigation: {
            styleOverrides: {
                root: {
                    boxShadow: '0 -2px 12px rgba(0,0,0,0.08)',
                },
            },
        },
        
        MuiLinearProgress: {
            styleOverrides: {
                root: {
                    borderRadius: designTokens.borderRadius.full,
                    backgroundColor: 'rgba(0,0,0,0.06)',
                },
                bar: {
                    borderRadius: designTokens.borderRadius.full,
                },
            },
        },
        
        MuiStepper: {
            styleOverrides: {
                root: {
                    padding: '24px 0',
                },
            },
        },
        
        MuiTab: {
            styleOverrides: {
                root: {
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '0.9375rem',
                    minHeight: 48,
                    transition: `all ${designTokens.transitions.fast}`,
                    '&:hover': {
                        color: '#2563eb',
                        backgroundColor: 'rgba(37,99,235,0.04)',
                    },
                },
            },
        },
        
        MuiFab: {
            styleOverrides: {
                root: {
                    boxShadow: designTokens.shadows.lg,
                    transition: `all ${designTokens.transitions.normal}`,
                    '&:hover': {
                        boxShadow: designTokens.shadows.xl,
                        transform: 'scale(1.05)',
                    },
                    '&:active': {
                        transform: 'scale(0.98)',
                    },
                },
            },
        },
    },
});

// Export design tokens for use in components
export { designTokens };
export default theme;
